import { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { CircleDollarSign, Plus, Pencil, Trash2 } from 'lucide-react';
import { useApp } from '@/store';
import { SectionId } from '@/lib/section-permissions';
import { useSectionRights } from '@/lib/use-section-rights';
import {
  DISPOSAL_TYPE,
  disposalContentFor,
  disposalTargetContent,
  disposalTypeFor,
  disposalTargetType,
  isDisposalType,
  rankContents,
} from '@/lib/billing';
import { type ServicePrice } from '@/data/types';
import { useT } from '@/lib/i18n';
import { useConfirm } from '@/components/ConfirmDialog';
import { fmtCurrency } from '@/lib/utils';
import { grossToNetInput, netToGrossInput } from '@/lib/spare-parts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type Form = {
  articleNr: string;
  description: string;
  deviceType: string;
  qtyRange: string;
  /** Net (excl. VAT) price — the only value ever persisted (client #3). */
  price: string;
  /** Mirror of `price` incl. VAT. Derived on every keystroke, never saved. */
  priceGross: string;
};
const blank = (vatPercent: number): Form => ({
  articleNr: '',
  description: '',
  deviceType: '',
  qtyRange: '1',
  price: '0',
  priceGross: netToGrossInput('0', vatPercent),
});
const fromPrice = (p: ServicePrice, vatPercent: number): Form => ({
  articleNr: p.articleNr,
  description: p.description,
  deviceType: p.deviceType,
  qtyRange: p.qtyRange,
  price: String(p.price),
  priceGross: netToGrossInput(String(p.price), vatPercent),
});

export function ServicePrices() {
  const t = useT();
  const confirm = useConfirm();
  const prices = useApp(s => s.servicePrices);
  const types = useApp(s => s.deviceTypes);
  const customers = useApp(s => s.customers);
  // Rows are stored as "disposal", "disposal:<deviceTypeId>" or
  // "disposal@<content>" (client #54).
  const disposalLabel = (deviceType: string) => {
    const target = disposalTargetType(deviceType);
    if (target) {
      const dt = types.find(x => x.id === target);
      return `${t('invoice.disposal')} · ${dt?.nameDe ?? target}`;
    }
    const content = disposalTargetContent(deviceType);
    if (content) return `${t('invoice.disposal')} · ${content}`;
    return deviceType === DISPOSAL_TYPE ? t('invoice.disposal') : deviceType;
  };
  // The client's disposal list is by size ("6 kg Pulver" CHF 15.00), which
  // only the device's content records — their device types carry no size. So
  // offer every content in the register, most-used first (client #54).
  const contentKeys = useMemo(
    () =>
      rankContents(
        customers.flatMap(c => (c.extinguishers ?? []).map(e => e.content))
      ).map(({ content }) => disposalContentFor(content)),
    [customers]
  );
  const settings = useApp(s => s.settings);
  const upsertServicePrice = useApp(s => s.upsertServicePrice);
  const deleteServicePrice = useApp(s => s.deleteServicePrice);
  const toast = useApp(s => s.toast);
  const can = useSectionRights()[SectionId.SERVICE_PRICES];

  const vatPercent = settings.vatPercent;
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ServicePrice | null>(null);
  const [form, setForm] = useState<Form>(() => blank(vatPercent));

  // Client #3: excl./incl. VAT sit side by side and each recalculates the
  // other. Only the field the user is NOT typing in gets rewritten — echoing
  // a rounded value back into the focused input makes the two fight over the
  // caret ("1.005" would snap to "1.00" mid-word).
  const setNetPrice = (value: string) =>
    setForm(f => ({
      ...f,
      price: value,
      priceGross: netToGrossInput(value, vatPercent),
    }));
  const setGrossPrice = (value: string) =>
    setForm(f => ({
      ...f,
      priceGross: value,
      price: grossToNetInput(value, vatPercent),
    }));

  const openCreate = () => {
    setEditing(null);
    setForm(blank(vatPercent));
    setOpen(true);
  };
  const openEdit = (p: ServicePrice) => {
    setEditing(p);
    setForm(fromPrice(p, vatPercent));
    setOpen(true);
  };
  const handleSave = () => {
    if (!form.articleNr.trim() || !form.deviceType) return;
    upsertServicePrice({
      articleNr: form.articleNr.trim(),
      description: form.description.trim(),
      deviceType: form.deviceType,
      qtyRange: form.qtyRange.trim(),
      price: parseFloat(form.price) || 0,
    });
    toast(editing ? t('common.save') : t('prices.newPrice'));
    setOpen(false);
  };
  const handleDelete = async (p: ServicePrice) => {
    if (!(await confirm(`${t('common.delete')} ${p.articleNr}?`))) return;
    deleteServicePrice(p.articleNr);
    toast(t('common.delete'));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <CircleDollarSign className="h-6 w-6 text-primary" />{' '}
            {t('prices.title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('prices.subtitle')}
          </p>
        </div>
        {can.create && (
          <Button size="sm" onClick={openCreate} data-focus="prices-new">
            <Plus className="h-4 w-4" />
            {t('prices.newPrice')}
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{t('prices.priceList')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('table.articleNr')}</TableHead>
                <TableHead>{t('table.deviceType')}</TableHead>
                <TableHead>{t('table.description')}</TableHead>
                <TableHead>{t('prices.qtyRange')}</TableHead>
                <TableHead className="text-right">{t('table.price')}</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {prices.map(p => {
                const dt = types.find(x => x.id === p.deviceType);
                return (
                  <TableRow key={p.articleNr}>
                    <TableCell className="font-mono text-xs">
                      {p.articleNr}
                    </TableCell>
                    <TableCell className="font-medium">
                      {dt?.nameDe ?? disposalLabel(p.deviceType)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {p.description}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {p.qtyRange}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {fmtCurrency(p.price, settings.currency)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {can.edit && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-11 w-11 sm:h-7 sm:w-7"
                            title={t('common.edit')}
                            aria-label={t('common.edit')}
                            onClick={() => openEdit(p)}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                        )}
                        {can.delete && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-11 w-11 sm:h-7 sm:w-7"
                            title={t('common.delete')}
                            aria-label={t('common.delete')}
                            onClick={() => handleDelete(p)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? t('common.edit') : t('prices.newPrice')}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
            <div>
              <Label>{t('table.articleNr')}</Label>
              <Input
                value={form.articleNr}
                onChange={e =>
                  setForm(f => ({ ...f, articleNr: e.target.value }))
                }
                disabled={!!editing}
                className="font-mono"
              />
            </div>
            <div>
              <Label>{t('table.deviceType')}</Label>
              <Select
                value={form.deviceType}
                onValueChange={v => setForm(f => ({ ...f, deviceType: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('table.deviceType')} />
                </SelectTrigger>
                <SelectContent>
                  {types.map(dt => (
                    <SelectItem key={dt.id} value={dt.id}>
                      {dt.nameDe}
                    </SelectItem>
                  ))}
                  {/* Taking a device away is charged like a service but is not
                      a device type, so it gets a reserved pseudo-type here —
                      otherwise the disposal fee could never be configured
                      and disposals billed a silent 0 (client #54). */}
                  <SelectItem value={DISPOSAL_TYPE}>
                    {t('invoice.disposal')}
                  </SelectItem>
                  {/* The fee differs per device — 2kg powder is not priced
                      like 12kg powder — so disposal can also be configured
                      against a single device type, falling back to the
                      generic row above (client #54). */}
                  {types.map(dt => (
                    <SelectItem
                      key={disposalTypeFor(dt.id)}
                      value={disposalTypeFor(dt.id)}
                    >
                      {t('invoice.disposal')} · {dt.nameDe}
                    </SelectItem>
                  ))}
                  {/* The client's list is by size, which the content records
                      and the device type does not; a content row wins over
                      both rows above. A row whose content no device carries
                      any more stays selectable so editing it keeps its key. */}
                  {[
                    ...contentKeys,
                    ...(disposalTargetContent(form.deviceType) &&
                    !contentKeys.includes(form.deviceType)
                      ? [form.deviceType]
                      : []),
                  ].map(key => (
                    <SelectItem key={key} value={key}>
                      {disposalLabel(key)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isDisposalType(form.deviceType) && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('prices.disposalPrecedence')}
                </p>
              )}
            </div>
            <div className="sm:col-span-2">
              <Label>{t('table.description')}</Label>
              <Input
                value={form.description}
                onChange={e =>
                  setForm(f => ({ ...f, description: e.target.value }))
                }
              />
            </div>
            <div>
              <Label>{t('prices.qtyRange')}</Label>
              <Input
                value={form.qtyRange}
                onChange={e =>
                  setForm(f => ({ ...f, qtyRange: e.target.value }))
                }
                placeholder="1-5" // i18n-ignore
              />
            </div>
            {/* Client #3 — paired VAT inputs; net is stored, gross is derived */}
            <div className="sm:col-span-2 grid gap-3 grid-cols-1 sm:grid-cols-2">
              <div>
                <Label htmlFor="service-price-net">{t('price.exclVat')}</Label>
                <Input
                  id="service-price-net"
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={e => setNetPrice(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="service-price-gross">
                  {t('price.inclVat')}
                </Label>
                <Input
                  id="service-price-gross"
                  type="number"
                  step="0.01"
                  value={form.priceGross}
                  onChange={e => setGrossPrice(e.target.value)}
                />
              </div>
              <p className="sm:col-span-2 text-xs text-muted-foreground">
                {t('price.vatPairHint')}: {vatPercent}%
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
